/**
 * Pillar 5 — What Changed panel.
 *
 * Construction-language list of every change for the active sheet pair.
 * Tapping a row jumps the stage to that bbox + offers Send-To-Foreman per change.
 *
 * NEVER says "raster" / "delta" / "matrix" — only "added" / "moved" /
 * "removed" / "changed" / "impact".
 */
import React, { useMemo, useState } from "react";
import { ChevronRight, Loader2, Send, CheckCircle2 } from "lucide-react";
import ImpactChip from "@/components/war_room/ImpactChip";
import { api } from "@/lib/api";
import { confirm } from "@/lib/confirm";


export default function WhatChangedPanel({ loading, payload, activeRow, focusedId, onFocus }) {
  // Group by severity (descending) and within severity by change_type.
  const grouped = useMemo(() => {
    const buckets = { critical: [], high: [], moderate: [], low: [], no: [] };
    for (const c of (payload?.changes || [])) {
      (buckets[c.severity] || buckets.no).push(c);
    }
    return buckets;
  }, [payload]);

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center text-sm text-[var(--fop-text-3)] gap-2" data-testid="what-changed-loading">
        <Loader2 className="animate-spin" size={14} /> Checking for changes…
      </div>
    );
  }
  if (!payload) {
    return (
      <div className="flex-1 flex items-center justify-center text-sm text-[var(--fop-text-3)]">
        Pick a sheet.
      </div>
    );
  }

  const summary = payload.summary || {};
  if (summary.no_prior_revision) {
    return (
      <div className="flex-1 flex items-center justify-center px-6 text-center" data-testid="what-changed-empty">
        <div>
          <div className="font-semibold text-[var(--fop-text)]">First revision</div>
          <div className="mt-1 text-xs text-[var(--fop-text-3)] max-w-xs">
            Only one revision of this sheet exists. There is nothing to compare yet.
          </div>
        </div>
      </div>
    );
  }
  if ((payload.changes || []).length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center px-6 text-center" data-testid="what-changed-clear">
        <div>
          <CheckCircle2 size={28} className="mx-auto text-[var(--fop-success)]" />
          <div className="mt-2 font-semibold text-[var(--fop-text)]">No changes detected</div>
          <div className="mt-1 text-xs text-[var(--fop-text-3)] max-w-xs">
            REV {summary.prior_revision} and REV {summary.current_revision} match on this drawing.
          </div>
        </div>
      </div>
    );
  }

  // Group by severity (descending) and within severity by change_type.

  return (
    <div className="flex-1 overflow-y-auto" data-testid="what-changed-panel">
      <div className="px-3 py-2 border-b border-[var(--fop-border)] sticky top-0 bg-[var(--fop-surface)] z-10">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--fop-text-3)]">
          What Changed · REV {summary.prior_revision} → REV {summary.current_revision}
        </div>
        <div className="text-[12px] text-[var(--fop-text-2)] mt-0.5">
          {summary.changes_total} change{summary.changes_total === 1 ? "" : "s"} ·{" "}
          {(summary.by_change_type?.added || 0)} added ·{" "}
          {(summary.by_change_type?.modified || 0)} changed ·{" "}
          {(summary.by_change_type?.removed || 0)} removed
        </div>
      </div>

      {["critical", "high", "moderate", "low", "no"].map((sev) => {
        const items = grouped[sev] || [];
        if (items.length === 0) return null;
        return (
          <div key={sev} className="px-2 py-2 border-b border-[var(--fop-border)] last:border-b-0">
            <div className="flex items-center gap-2 px-1 pb-1">
              <ImpactChip severity={sev} count={items.length} testid={`what-changed-group-${sev}`} />
            </div>
            {items.map((c) => (
              <ChangeRow
                key={c.id}
                change={c}
                focused={focusedId === c.id}
                onFocus={() => onFocus?.(c)}
              />
            ))}
          </div>
        );
      })}
    </div>
  );
}


function ChangeRow({ change, focused, onFocus }) {
  const [sending, setSending] = useState(false);

  const sendOne = async (e) => {
    e.stopPropagation();
    setSending(true);
    try {
      await api.post(
        `/sheets/${change.sheet_id}/changes/${change.id}/send-to-foreman`,
        { summary_text: change.summary },
      );
      const url = `${window.location.origin}/sheet/${change.sheet_id}?focus=change:${change.id}`;
      const text = `${change.summary} · ${change.field_impact}`;
      if (navigator.share) {
        try {
          await navigator.share({ title: "ForgedOps — change", text, url });
        } catch (err) { if (err?.name !== "AbortError") throw err; }
      } else {
        await navigator.clipboard.writeText(`${text}\n${url}`);
      }
      confirm.raw?.success?.("Sent to foreman");
    } catch (err) {
      confirm.raw?.error?.(err?.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  return (
    <button
      onClick={onFocus}
      className={`w-full text-left mt-1 px-2 py-2 rounded transition-colors border ${
        focused
          ? "border-[var(--fop-primary)]/60 bg-[var(--fop-primary-soft)]"
          : "border-transparent hover:bg-[var(--fop-surface-2)] hover:border-[var(--fop-border)]"
      }`}
      data-testid={`what-changed-row-${change.id}`}
    >
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <div className="text-[12px] font-semibold text-[var(--fop-text)] truncate">
            {change.summary}
          </div>
          <div className="text-[10px] font-mono uppercase tracking-wider text-[var(--fop-text-3)] mt-0.5">
            {change.category} · {change.verb} · {change.field_impact}
          </div>
          <div className="text-[10px] text-[var(--fop-text-3)] mt-0.5 leading-snug">
            {change.reasoning}
          </div>
        </div>
        <button
          type="button"
          onClick={sendOne}
          disabled={sending}
          className="shrink-0 inline-flex items-center gap-1 px-1.5 py-1 rounded text-[10px] font-mono uppercase tracking-wider text-[var(--fop-primary)] hover:bg-[var(--fop-primary-soft)] disabled:opacity-50"
          data-testid={`what-changed-send-${change.id}`}
          title="Send to foreman"
        >
          <Send size={11} /> Send
        </button>
        <ChevronRight size={12} className="text-[var(--fop-text-3)] mt-1" />
      </div>
    </button>
  );
}
